import { Component, Input, OnInit, AfterViewInit, ViewChild, Inject, Output, EventEmitter, Optional } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatLegacyDialogModule as MatDialogModule, MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NgxUiLoaderService, NgxUiLoaderModule } from 'ngx-ui-loader';
import { SnackBarService } from '../../../ui-services/snackbar/snackBar.service';
import { ScheduledEmailsService } from './scheduled-emails.service';
import { GridBase } from 'src/app/services/tables/GridBase';
import { SharedService } from 'src/app/layouts/shared-service';
import { localStorageAccessConstant } from 'src/app/constants/LocalStorageAccessConstant';
import { EmailSchedule } from 'src/app/models/scheduled-emails/email-schedule';
import { ScheduleFrequency } from 'src/app/enums/email.enum';
import { ScheduleEmailDialogWrapperComponent } from '../group-analytics-email-pricing-dialog/schedule-email/schedule-email-dialog-wrapper.component';
import { TooltipListPipe } from "../../../shared/pipes/tooltip/tooltip-list.pipe";
import { AppPipesModule } from "../../../app-pipes.module";
import { UtcToLocalPipe } from "../../../shared/pipes/dateTime/utc-to-local.pipe";
import { DeleteConfirmationComponent, DeleteConfirmationData } from '../../../shared/components/delete-confirmation/delete-confirmation.component';
import { GlobalSearchComponent, GlobalSearchConfig, SearchField } from '../../../shared/components/global-search/global-search.component';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';

type EmailScheduleGrid = EmailSchedule & { isSelected?: boolean };
@Component({
  selector: 'app-manage-scheduled-emails',
  templateUrl: './manage-scheduled-emails.component.html',
  styleUrls: ['./manage-scheduled-emails.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatToolbarModule,
    NgxUiLoaderModule,
    TooltipListPipe,
    AppPipesModule,
    GlobalSearchComponent
],
  providers: [
    ScheduledEmailsService,
    SnackBarService,
    SharedService,
    UtcToLocalPipe
  ]
})
export class ManageScheduledEmailsComponent extends GridBase implements OnInit, AfterViewInit {
  @Input() customerId: number = 0;
  @Output() generateEmail = new EventEmitter<void>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = ['select', 'groupName', 'scheduleDateTime', 'timeZone', 'frequency', 'contactEmails', 'edit', 'delete'];
  
  
  // Selection
  selectedItems: EmailScheduleGrid[] = [];
  allSelected: boolean = false;
  
  // Sample data observable
  scheduledEmails$: Observable<EmailScheduleGrid[]>;
  groupId: number = 0;

  public fullData: EmailScheduleGrid[] = [];
  
  // Loader configuration
  public readonly loaderId = 'manage-scheduled-emails';
  
  customers: any[] = [];
  frequencyValues = Object.values(ScheduleFrequency) as string[];
  frequencyFilterOptions = Object.values(ScheduleFrequency).map((value, index) => ({
    label: value,
    value: index
  }));

  // Global search configuration
  searchConfig: GlobalSearchConfig = {
    placeholder: 'Search scheduled emails...',
    debounceTime: 300,
    searchFields: [
      {
        key: 'groupName',
        label: 'Group Name',
        searchable: true
      },
      {
        key: 'scheduleDateTime',
        label: 'Schedule Date & Time',
        searchable: true,
        transform: (value: Date | string | null | undefined) => this.formatDateForSearch(value)
      },
      {
        key: 'timeZone',
        label: 'Time Zone',
        searchable: true
      },
      {
        key: 'frequency',
        label: 'Frequency',
        searchable: true,
        transform: (value: number) => this.frequencyValues[value] || ''
      },
      {
        key: 'contactEmails',
        label: 'Contact Emails',
        searchable: true,
        transform: (value: string[]) => this.formatContactEmailsForSearch(value)
      }
    ],
    customFilterPredicate: (data: EmailScheduleGrid, filter: string) => {
      if (!filter) return true;
      const searchTerm = filter.toLowerCase();
      
      const searchableFields = [
        data.groupName || '',
        this.formatDateForSearch(data.scheduleDateTime) || '',
        data.timeZone || '',
        this.frequencyValues[data.frequency] || '',
        this.formatContactEmailsForSearch(data.contactEmails) || ''
      ];

      return searchableFields.some(field => 
        field.toLowerCase().includes(searchTerm)
      );
    }
  };

  constructor(
    private snackBarService: SnackBarService,
    private scheduledEmailsService: ScheduledEmailsService,
    private sharedService: SharedService,
    private ngxLoader: NgxUiLoaderService,
    private utcToLocalPipe: UtcToLocalPipe,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private appDialogWrapperService: AppDialogWrapperService
  ) {
    super();
    this.groupId = Number(this.sharedService.getCurrentUserPropertyValue(localStorageAccessConstant.groupId));
    // Initialize with service data
    this.scheduledEmails$ = this.scheduledEmailsService.getScheduleEmailsByGroup(this.groupId);
    // Ensure dataSource is initialized
    this.dataSource = new MatTableDataSource<EmailScheduleGrid>([]);
  }

  ngOnInit(): void {
    this.loadData();
    
    // Get customers from injected dialog data
    if (this.data && this.data.customers) {
      this.customers = this.data.customers;
    }
  }

  ngAfterViewInit(): void {
    // Initialize virtual scroll variables after view is initialized
      this.setVirtualScrollVariables(this.paginator, this.sort, this.dataSource.data);
  }

  /**
   * Load data from the observable
   */
  loadData(): void {
    this.ngxLoader.startLoader(this.loaderId);
    this.scheduledEmails$.pipe(
      catchError(error => {
        this.ngxLoader.stopLoader(this.loaderId);
        this.snackBarService.showErrorSnackBar('Failed to load scheduled emails');
        console.error('Error loading scheduled emails:', error);
        return of([]);
      })
    ).subscribe(emails => {
      this.ngxLoader.stopLoader(this.loaderId); 
      
      this.dataSource.data = emails;    
      this.fullData = emails;
      this.updateSelectionState();
    });
  }


  /**
   * Format date for search (same format as displayed)
   */
  private formatDateForSearch(date: Date | string | null | undefined): string {
    if (!date) return '';
    
    // Convert string to Date if needed
    let dateObj: Date;
    if (typeof date === 'string') {
      dateObj = new Date(date);
    } else {
      dateObj = date;
    }
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(dateObj);
    } catch (error) {
      // If formatting fails, return empty string
      return '';
    }
  }

  /**
   * Format contact emails for search
   */
  private formatContactEmailsForSearch(emails: string[]): string {
    if (!emails || emails.length === 0) return 'No emails';
    return emails.join(' ');
  }

  /**
   * Handle search change from global search component
   */
  onSearchChange(searchValue: string): void {
    this.updateSelectionState();
  }

  /**
   * Handle search cleared from global search component
   */
  onSearchCleared(): void {
    this.updateSelectionState();
  }

  /**
   * Toggle selection of all items
   */
  toggleAllSelection(): void {
    this.allSelected = !this.allSelected;
    // Get filtered data for selection
    const filteredData = this.dataSource.filteredData || this.dataSource.data;
    filteredData.forEach(item => {
      item.isSelected = this.allSelected;
    });
    this.updateSelectedItems();
  }

  /**
   * Toggle selection of individual item
   */
  toggleItemSelection(item: EmailScheduleGrid): void {
    item.isSelected = !item.isSelected;
    this.updateSelectedItems();
    this.updateSelectionState();
  }

  /**
   * Update the selected items array
   */
  private updateSelectedItems(): void {
    this.selectedItems = this.dataSource.data.filter(item => item.isSelected);
  }

  /**
   * Update the selection state (all selected, none selected, or partial)
   */
  private updateSelectionState(): void {
    // Use filtered data for selection state
    const filteredData = this.dataSource.filteredData || this.dataSource.data;
    const totalItems = filteredData.length;
    const selectedCount = filteredData.filter(item => item.isSelected).length;
    
    if (selectedCount === 0) {
      this.allSelected = false;
    } else if (selectedCount === totalItems) {
      this.allSelected = true;
    } else {
      this.allSelected = false;
    }
  }

  /**
   * Create new scheduled email
   */
  createNewEmail(): void {
    // Emit event to parent - the parent will handle closing the dialog
    this.generateEmail.emit();
  }

  /**
   * Edit scheduled email
   */
  editEmail(item: EmailSchedule): void {
    // Prevent editing disabled rows
    if (this.isRowDisabled(item)) {
      this.snackBarService.showErrorSnackBar('Cannot edit past one-time scheduled emails');
      return;
    }

    // Create a clean copy of the item to avoid circular references
    const cleanItem = {
      oid: item.oid,
      frequency: item.frequency,
      timeZone: item.timeZone,
      scheduleDateTime: item.scheduleDateTime,
      nextPlannedUtc: item.nextPlannedUtc,
      contactEmails: item.contactEmails,
      customerId: item.customerId
    };

    const dialogWrapper = this.appDialogWrapperService.open<EmailSchedule>({
      title: 'Edit Scheduled Email',
      component: ScheduleEmailDialogWrapperComponent,
      data: {
        emailSchedule: cleanItem,
      },
      width: '600px',
      height: 'auto'
    });

    dialogWrapper.afterClosed().subscribe((result: EmailSchedule) => {
      if (!result) {
        return; // User cancelled
      }
      // Update row in-place to reflect new values from the dialog
      const idx = this.dataSource.data.findIndex(x => x.oid === item.oid);
      if (idx > -1) {
        const row = this.dataSource.data[idx] as EmailScheduleGrid; 
        row.frequency = result.frequency;
        row.timeZone = result.timeZone; 
        row.scheduleDateTime = result.scheduleDateTime;
        row.nextPlannedUtc = result.nextPlannedUtc;
        
        // Force table refresh
        this.dataSource.data = [...this.dataSource.data];
      }
      this.snackBarService.showSuccessSnackBar('Email schedule updated successfully');
    });
  }

  /**
   * Delete single scheduled email (deletes all related records with same CorrelationId)
   */
  deleteEmail(item: EmailScheduleGrid): void {
    const dialogData: DeleteConfirmationData = {
      item: item,
      description: 'scheduled email',
      fullDescription: 'Are you sure you want to delete this scheduled email and all related records? This action cannot be undone.',
      includeThis: false
    };

    const dialogWrapper = this.appDialogWrapperService.open<DeleteConfirmationData>({
      component: DeleteConfirmationComponent,
      data: dialogData,
      width: '400px',
      hideHeader: true
    });

    dialogWrapper.afterClosed().subscribe(result => {
      if (result) {
        this.ngxLoader.startLoader(this.loaderId);
        this.scheduledEmailsService.deleteScheduleEmail(item.oid).subscribe(
          () => {
            this.ngxLoader.stopLoader(this.loaderId);
            // Remove the deleted item from data source
            const index = this.dataSource.data.findIndex(email => email.oid === item.oid);
            if (index > -1) {
              this.dataSource.data.splice(index, 1);
              this.dataSource._updateChangeSubscription();
              this.snackBarService.showSuccessSnackBar(`Scheduled email and all related records deleted successfully`);
            }
          },
          (error) => {
            this.ngxLoader.stopLoader(this.loaderId);
            this.snackBarService.showErrorSnackBar('Failed to delete scheduled email');
            console.error('Error deleting scheduled email:', error);
          }
        );
      }
    });
  }

  /**
   * Delete selected emails (deletes all related records with same CorrelationId)
   */
  deleteSelectedEmails(): void {
    if (this.selectedItems.length === 0) {
      this.snackBarService.showErrorSnackBar('No emails selected for deletion');
      return;
    }

    const dialogData: DeleteConfirmationData = {
      item: this.selectedItems,
      description: `${this.selectedItems.length} selected emails`,
      fullDescription: `Are you sure you want to delete ${this.selectedItems.length} selected emails and all their related records? This action cannot be undone.`,
      includeThis: false
    };

    const dialogWrapper = this.appDialogWrapperService.open<DeleteConfirmationData>({
      title: 'Delete Selected Emails',
      component: DeleteConfirmationComponent,
      data: dialogData,
      width: '400px',
      hideHeader: true
    });

    dialogWrapper.afterClosed().subscribe(result => {
      if (result) {
        this.ngxLoader.startLoader(this.loaderId);
        const selectedIds = this.selectedItems.map(item => item.oid);
        
        this.scheduledEmailsService.deleteMultipleScheduleEmails(selectedIds).subscribe({
          next: (success) => {
            this.ngxLoader.stopLoader(this.loaderId);
            if (success) {
              // Remove selected items from data source
              this.selectedItems.forEach(selectedItem => {
                const index = this.dataSource.data.findIndex(email => email.oid === selectedItem.oid);
                if (index > -1) {
                  this.dataSource.data.splice(index, 1);
                }
              });
              
              this.dataSource._updateChangeSubscription();
              this.selectedItems = [];
              this.allSelected = false;
              this.snackBarService.showSuccessSnackBar(`${selectedIds.length} emails and all related records deleted successfully`);
            }
          },
          error: (error) => {
            this.ngxLoader.stopLoader(this.loaderId);
            this.snackBarService.showErrorSnackBar('Failed to delete selected emails');
            console.error('Error deleting selected emails:', error);
          }
        });
      }
    });
  }

  /**
   * Cancel selection
   */
  cancelSelection(): void {
    this.selectedItems.forEach(item => {
      item.isSelected = false;
    });
    this.selectedItems = [];
    this.allSelected = false;
    this.snackBarService.showSuccessSnackBar('Selection cancelled');
  }

  /**
   * Check if a row should be disabled (OneTime frequency with past schedule date)
   */
  isRowDisabled(row: EmailScheduleGrid): boolean {
    if (row.frequency !== ScheduleFrequency.OneTime) {
      return false;
    }
    
    if (!row.scheduleDateTime) {
      return false;
    }
    
    const scheduleDate = new Date(row.scheduleDateTime);
    const now = new Date();
    
    return scheduleDate < now;
  }
}
